import type { VocRecord } from '@/types/vocData'

export interface JTBDNode {
  name: string;
  description?: string;
  mentionCount?: number;
  satisfactionScore?: number;
  children?: JTBDNode[];
}

/**
 * 从 VocRecord 数组构建 JTBD 四层树结构
 * root → journey → task → subTask → bestPractice（叶节点）
 */
export function buildJTBDTree(records: VocRecord[], rootName: string): JTBDNode {
  // 四层嵌套 Map: journey → task → subTask → bpLabel → { count, starSum, desc }
  const tree = new Map<string, Map<string, Map<string, Map<string, { count: number; starSum: number; desc: string }>>>>();

  for (const record of records) {
    const { journey, task, subTask, bestPractice, starRating } = record;
    if (!journey || !task || !subTask || !bestPractice) continue;

    const colonIdx = bestPractice.search(/[：:]/);
    const bpLabel = colonIdx === -1 ? bestPractice : bestPractice.slice(0, colonIdx);
    const bpDesc = colonIdx === -1 ? '' : bestPractice.slice(colonIdx + 1);

    if (!tree.has(journey)) tree.set(journey, new Map());
    const journeyMap = tree.get(journey)!;

    if (!journeyMap.has(task)) journeyMap.set(task, new Map());
    const taskMap = journeyMap.get(task)!;

    if (!taskMap.has(subTask)) taskMap.set(subTask, new Map());
    const subTaskMap = taskMap.get(subTask)!;

    if (!subTaskMap.has(bpLabel)) subTaskMap.set(bpLabel, { count: 0, starSum: 0, desc: bpDesc });
    const stats = subTaskMap.get(bpLabel)!;

    stats.count++;
    stats.starSum += starRating;
  }

  const root: JTBDNode = { name: rootName, children: [] };

  for (const [journeyName, journeyMap] of tree) {
    const journeyNode: JTBDNode = { name: journeyName, children: [] };

    for (const [taskName, taskMap] of journeyMap) {
      const taskNode: JTBDNode = { name: taskName, children: [] };

      for (const [subTaskName, bpMap] of taskMap) {
        const subTaskNode: JTBDNode = { name: subTaskName, children: [] };

        for (const [bpLabel, stats] of bpMap) {
          subTaskNode.children!.push({
            name: bpLabel,
            description: stats.desc,
            mentionCount: stats.count,
            satisfactionScore: stats.count === 0 ? 3 : stats.starSum / stats.count,
          });
        }

        taskNode.children!.push(subTaskNode);
      }

      journeyNode.children!.push(taskNode);
    }

    root.children!.push(journeyNode);
  }

  return root;
}
